import { type Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { type CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea, CampusSection } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { CampusSectionFilter } from '@chancenwerk/campus-learning-content/entities/CampusSectionFilter.js';
import { CampusSubject } from '@chancenwerk/db/enums/CampusSubject.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-link.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, filter, switchMap } from 'rxjs';
import './section-lesson-list/campus-component-section-card.js';

import { acss } from '@adornis/chemistry/directives/acss.js';
import { LearningDataAPI } from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import g_logoBiologie from '../assets/Biologie_Alpha.png';
import g_logoChemie from '../assets/Chemie_Alpha.png';
import g_logoPolitik from '../assets/Politik.png';
import g_logoLandingOne from '../assets/landing_1_alpha.png';
import g_logoLandingTwo from '../assets/landing_2_alpha.png';
import g_logoLandingThree from '../assets/landing_3_alpha.png';

const subjectLogos = {
  [CampusSubject.DEUTSCH]: g_logoLandingOne,
  [CampusSubject.ENGLISCH]: g_logoLandingTwo,
  [CampusSubject.MATHE]: g_logoLandingThree,
  [CampusSubject.POLITIK]: g_logoPolitik,
  [CampusSubject.BIOLOGIE]: g_logoBiologie,
  [CampusSubject.CHEMIE]: g_logoChemie,
};

/**
 * @customElement campus-component-teacher-dashboard-lesson-list
 */
@customElement('campus-component-teacher-dashboard-lesson-list')
export class CampusComponentTeacherDashboardLessonList extends ChemistryLitElement {
  @state() public markedLessonIDs = new RXController<string[]>(this, [] as string[]);

  @property({ attribute: false }) public area = new RXController(this, CampusArea.STUDENTS);
  @property({ attribute: false }) public selector = new RXController(this, 'all');
  @property({ attribute: false }) public showSearchBar = new RXController(this, true);
  @property({ attribute: false }) public student = new RXController<Maybe<Student>>(this, null);

  private readonly _sectionsPaginated = new SubscriptionPagination<
    CampusSection,
    Array<CampusSectionFilter | undefined>
  >(
    LearningContentAPI.subscribeSectionsByFilter.bind(CampusSection),
    [new CampusSectionFilter({})],
    {
      name: 1,
      subjects: 1,
      hiddenFromStudents: 1,
      lessonIDs: 1,
      lessons: {
        name: 1,
        hiddenFromStudents: 1,
        lessonIDsDependingOn: 1,
        sortingOrder: 1,
        exerciseIDs: 1,
      },
    },
    20,
    20,
  );

  @state() private readonly _sections = new RXController(this, this._sectionsPaginated.data);

  @state() private readonly _sectionsLoading = new RXController(this, this._sectionsPaginated.loadingInitially);
  @state() private readonly _openSubjects = new RXController<string[]>(this, []);
  @state() private readonly _showSubjects = new RXController(
    this,
    this.student.observable.pipe(
      switchMap(async u => {
        return LearningDataAPI.getSubjectsWithDiagnostics(u?._id ?? '')();
      }),
    ),
  );

  // Filtering properties
  @state() public userCheckedSubjects = new RXController<string[]>(this, []);
  @state() public userCheckedGrades = new RXController<number[]>(this, []);
  @state() public userSearchText = new RXController<string>(this, '');
  @state() public showOnlyMarked = new RXController<boolean>(this, false);
  @state() public userHiddenFromStudents = new RXController<Maybe<boolean>>(this, null);

  override connectedCallback(): void {
    super.connectedCallback();

    combineLatest([
      this.userCheckedSubjects.observable,
      this.userCheckedGrades.observable,
      this.userSearchText.observable,
      this.userHiddenFromStudents.observable,
      this.selector.observable,
      this.student.observable,
      this.area.observable,
      this.showOnlyMarked.observable,
    ])
      .pipe(filter(Boolean))
      .subscribe(
        ([checkedSubjects, checkedGrades, searchText, hiddenFromStudents, selector, user, area, onlyMarked]) => {
          this._sectionsPaginated.updateParameters(
            new CampusSectionFilter({
              userSearchText: searchText,
              userCheckedSubjects: checkedSubjects,
              userCheckedGrades: checkedGrades,
              hiddenFromStudents,
              userIsGrade:
                user?._class === Student._class ? (user as Student).schoolInformation.jahrgangsstufe : undefined,
              showAssignedLessons: selector === 'assigned',
              userID: user ? user._id : '',
              playMode: false,
              lessonIDs: onlyMarked ? this.markedLessonIDs.value : undefined,
              area,
            }),
          );
        },
      );
  }

  @state() public hideLessonsIDs = new RXController<string[]>(this, []);

  override render() {
    return html` <cr-flex space="sm"> ${this._getSectionList()} </cr-flex> `;
  }

  private _getSectionList() {
    if (this.area.value !== CampusArea.STUDENTS) {
      return html`<cr-flex>
        ${repeat(
          this._sections.value,
          section => section._id,
          section => {
            return html` <campus-component-section-card
              ${css({ height: '100%', display: 'block' })}
              .userID=${this.student.value?._id}
              .showOnlyAssigned=${this.selector.value === 'assigned'}
              .sectionID=${section._id}
              .area=${this.area.value}
              .userSearchText=${this.userSearchText.value}
              .userHiddenFromStudents=${this.userHiddenFromStudents.value}
              .markedLessonIDs=${this.markedLessonIDs.value}
              .hideLessonsIDs=${this.hideLessonsIDs.value}
              .showOnlyMarked=${this.showOnlyMarked.value}
              .showOnlyStarted=${true}
              ?condensed=${true}
              dashboard
            ></campus-component-section-card>`;
          },
        )}
      </cr-flex>`;
    }

    if (this._showSubjects.value?.length === 0) {
      return html`<cr-text>Der Schüler hat noch keine Lektion bearbeitet.</cr-text>`;
    }

    return html`${repeat(
      Object.keys(subjectLogos),
      subject => subject,
      subject => {
        if (!this._showSubjects.value?.includes(subject)) return nothing;

        const isOpen = this._openSubjects.value.includes(subject);
        const sections = this._sections.value?.filter(
          section => !section.hiddenFromStudents && section.subjects.includes(subject),
        );

        if (!sections || sections.length === 0) {
          return nothing;
        }

        return html`
          <cr-flex
            padding="md"
            space="md"
            horizontal
            space-between
            crossaxis-center
            ${css({ backgroundColor: this.colors.neutral.shade(100), borderRadius: this.sizes.borderRadiusSecondary })}
            ${acss({
              cursor: 'pointer',
              '&:hover': { scale: 1.01 },
            })}
            @click=${() => {
              this._openSubjects.next(
                isOpen ? this._openSubjects.value.filter(s => s !== subject) : [...this._openSubjects.value, subject],
              );
            }}
          >
            <cr-h3>${subject}</cr-h3>
            <cr-image
              .value=${subjectLogos[subject]}
              mode="contain"
              ${css({ width: '64px', height: '64px' })}
              disabled
            ></cr-image>
          </cr-flex>
          ${isOpen
            ? repeat(
                sections,
                section => section._id,
                section => {
                  return html` <campus-component-section-card
                    ${css({ height: '100%', display: 'block' })}
                    .userID=${this.student.value?._id}
                    .showOnlyAssigned=${this.selector.value === 'assigned'}
                    .sectionID=${section._id}
                    .area=${this.area.value}
                    .userSearchText=${this.userSearchText.value}
                    .userHiddenFromStudents=${this.userHiddenFromStudents.value}
                    .markedLessonIDs=${this.markedLessonIDs.value}
                    .hideLessonsIDs=${this.hideLessonsIDs.value}
                    .showOnlyMarked=${this.showOnlyMarked.value}
                    .showOnlyStarted=${true}
                    ?condensed=${true}
                    dashboard
                  ></campus-component-section-card>`;
                },
              )
            : nothing}
        `;
      },
    )}`;
  }

  private _filterLessons(localLessons: CampusLesson[], section: CampusSection): CampusLesson[] {
    if (this.showOnlyMarked.value) {
      localLessons = localLessons.filter(lesson => this.markedLessonIDs.value.includes(lesson._id));
    }

    // Textfilter for searchbar
    if ((this.userSearchText.value || '').length) {
      localLessons = section.name.toLocaleLowerCase().includes(this.userSearchText.value.toLocaleLowerCase())
        ? localLessons
        : localLessons.filter(lesson =>
            lesson.name.toLocaleLowerCase().includes(this.userSearchText.value.toLocaleLowerCase()),
          );
    }

    if (typeof this.userHiddenFromStudents.value === 'boolean') {
      localLessons = localLessons.filter(lesson => {
        return lesson.hiddenFromStudents === this.userHiddenFromStudents.value;
      });
    }

    // Filter only assigned lessons
    localLessons = localLessons.filter(lesson => !this.hideLessonsIDs.value.includes(lesson._id));

    return localLessons;
  }
}
